import { AuthGate } from "@components/auth/AuthGate";
import { RootErrorBoundary } from "@components/error/RootErrorBoundary";
import { RouteErrorFallback } from "@components/error/RouteErrorFallback";
import { HotkeysProvider } from "@components/hotkeys/HotkeysProvider";
import { db } from "@db";
import { autoEmptyTrash } from "@db/trash";
import { useFeatureUsageStore } from "@help/featureUsageStore";
import { LocalStorage } from "@kinde/js-utils";
import { KindeProvider } from "@kinde-oss/kinde-auth-react";
import * as Sentry from "@sentry/react";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import ReactDOM from "react-dom/client";
import "./main.css";
import { routeTree } from "./routeTree.gen";

await useFeatureUsageStore.getState().hydrate();

const settings = await db.settings.get("settings");
if (settings) {
  await autoEmptyTrash(settings.autoEmptyTrashDays);
}

if (import.meta.env.PROD) {
  Sentry.init({
    dsn: "https://449fe460b3d6ee0fc7b7720ad35f727a@o4511027068469248.ingest.de.sentry.io/4511027070238800",
    sendDefaultPii: true,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
}

const kindeStore = new LocalStorage();

// Set up a Router instance
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultErrorComponent: RouteErrorFallback,
});

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("app");

if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <RootErrorBoundary>
      <KindeProvider
        clientId={import.meta.env.VITE_KINDE_CLIENT_ID}
        domain={import.meta.env.VITE_KINDE_DOMAIN}
        audience={import.meta.env.VITE_API_URL}
        redirectUri={window.location.origin}
        logoutUri={window.location.origin}
        store={kindeStore}
      >
        <AuthGate>
          <HotkeysProvider>
            <RouterProvider router={router} />
          </HotkeysProvider>
        </AuthGate>
      </KindeProvider>
    </RootErrorBoundary>,
  );
}
